import { trackAnalyticsEvent } from '@pm/analytics';
import { CoreMessageFieldsFragment } from '@pm/graphql';
import { useMarkdownComponents } from '@pm/ui/src/hooks/use-markdown-components/use-markdown-components';
import { parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { useMessageName } from '../../hooks/useMessageName';
import { Attachments } from '../Attachments';
import { DateTime } from '../DateTime';
import MessageName from '../MessageName';

type MessageProps = {
  message: CoreMessageFieldsFragment;
  userId: string;
  brand: string;
};

const Message = ({ message, userId, brand }: MessageProps) => {
  const senderName = useMessageName(brand, message.sender);
  const { p, ul, a, ol } = useMarkdownComponents({ variant: 'subdued' });
  const { t } = useTranslation('messages');

  const content = useMemo(() => {
    if (message.deletedAt) {
      return t('MessageDeleted', {
        date: parseISO(message.deletedAt),
      });
    }

    if (message.content === 'Attachments' && message.attachments.length > 0) {
      return t('Attachments');
    }

    return message.content;
  }, [message, t]);

  return (
    <article className="mt-3 w-full shadow">
      <div className="bg-grey-50 flex items-center">
        <MessageName variant="sender">{senderName}</MessageName>
        <div className="text-grey-400 flex-1 p-4 text-right text-sm leading-8">
          <span className="ml-2">
            <DateTime value={message.createdAt} format="Pp" />
          </span>
        </div>
      </div>
      <div className="text-md bg-white px-4 py-6 sensitive space-y-s">
        <ReactMarkdown
          components={{
            p,
            a,
            ul,
            ol,
          }}
          rehypePlugins={[rehypeRaw]}
        >
          {content}
        </ReactMarkdown>
      </div>
      {'attachments' in message && (
        <Attachments
          attachments={message.attachments}
          handleAttachmentClick={() =>
            trackAnalyticsEvent('File downloaded', {
              userId,
              date: new Date().toLocaleString(),
              source: {
                category: 'Secure Message',
                messageId: message.id,
              },
            })
          }
        />
      )}
    </article>
  );
};

export { Message };
